import { FieldError } from 'react-hook-form'
import { ReactNode, ChangeEvent, forwardRef } from 'react'
import { Text, Flex, TextAsProps } from 'components/elements'
import { keyframes, styled, CSS } from 'theme'
import Avatar from 'components/avatar'
import { placeholderProps } from 'components/dropdown'

export interface CheckBoxProps {
  name: string
  asLabel?: TextAsProps
  css?: CSS
  dropdownCheckBox?: placeholderProps
  label?: string | ReactNode
  description?: string
  optionalText?: ReactNode
  error?: FieldError | string
  value: boolean
  setValue?: (checked: boolean) => void
  listing?: number
  onChange?: (element: ChangeEvent<HTMLInputElement>) => void
}

export const CheckBox = forwardRef<HTMLInputElement, CheckBoxProps>(
  (
    {
      name,
      asLabel,
      optionalText,
      css,
      dropdownCheckBox,
      value,
      setValue,
      onChange,
      label,
      description,
      error,
    },
    ref
  ) => {
    const { text, subLabel } = dropdownCheckBox || {}

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
      onChange?.(event)
      const newChecked = event.target.checked
      setValue?.(newChecked)
    }

    const DropdownCheckCss: CSS = text
      ? {
          'input[type="checkbox"]': {
            '& ~ label': {
              'flexDirection': 'row-reverse',
              'span': {
                mr: 0,
                ml: '$sapce$8',
                background: 'transparent',
                border: 'none',
              },
              '&:hover': {
                span: {
                  background: 'transparent',
                },
              },
            },
            '&:checked': {
              '& ~ label': {
                'background': '$gray50',
                'span': {
                  'background': 'transparent',
                  'borderColor': 'transparent',
                  '&:before': {
                    top: 9,
                    left: 3,
                    animationName: `${checkOnly}`,
                  },
                },
                '&:hover': {
                  background: '$gray100',
                  span: {
                    background: 'transparent',
                    borderColor: 'transparent',
                  },
                },
              },
            },
          },
        }
      : {}

    return (
      <InputBoxWrap direction={'column'} css={DropdownCheckCss} ref={ref}>
        <InputCss
          id={name}
          type="checkbox"
          onChange={handleChange}
          checked={value}
        />
        <Text as="label" htmlFor={name} css={{ ...css, mb: 0 }}>
          <Text />
          {!!text ? (
            <Avatar
              size={'sm'}
              title={text}
              subTitle={subLabel}
              direction="row"
              css={{ flexGrow: 1 }}
            />
          ) : (
            <>
              <Text as={asLabel || 'h6'} css={{ pr: 3 }}>
                {label}
              </Text>
              {!!description && (
                <Text as="p" css={{ ml: 28 }} children={description} />
              )}
              {!!optionalText && optionalText}
            </>
          )}
        </Text>
        {!!error && (
          <Text
            color="error"
            css={{ p: '0 $space$20 $space$15', textAlign: 'start' }}
            className="errorField"
          >
            {error as ReactNode}
          </Text>
        )}
      </InputBoxWrap>
    )
  }
)

export const shrinkBounce = keyframes({
  '0%': {
    transform: 'scale(1)',
  },
  '33%': {
    transform: 'scale(.85)',
  },
  '100%': {
    transform: 'scale(1)',
  },
})

export const circleCheckBox = keyframes({
  '0%': {
    width: 0,
    height: 0,
    borderColor: '$whiteBase',
    transform: 'translate3d(0,0,0) rotate(45deg)',
  },
  '33%': {
    width: 5,
    height: 0,
    transform: 'translate3d(0,0,0) rotate(45deg)',
  },
  '100%': {
    width: 5,
    height: 11,
    borderColor: '$whiteBase',
    transform: 'translate3d(0, -11px, 0) rotate(45deg)',
  },
})

const checkboxCheck = keyframes({
  '0%': {
    width: 0,
    height: 0,
    borderColor: '$checkboxCheck',
    transform: 'translate3d(0,0,0) rotate(45deg)',
  },
  '33%': {
    width: 5,
    height: 0,
    transform: 'translate3d(0,0,0) rotate(45deg)',
  },
  '100%': {
    width: 5,
    height: 11,
    borderColor: '$checkboxCheck',
    transform: 'translate3d(0, -11px, 0) rotate(45deg)',
  },
})

export const checkOnly = keyframes({
  '0%': {
    width: 0,
    height: 0,
    borderColor: '$checkboxCheck',
    transform: 'translate3d(0,0,0) rotate(45deg)',
  },
  '33%': {
    width: 7,
    height: 0,
    transform: 'translate3d(0,0,0) rotate(45deg)',
  },
  '100%': {
    width: 7,
    height: 15,
    borderColor: '$checkboxCheck',
    transform: 'translate3d(0, -13px, 0) rotate(45deg)',
  },
})

export const InputCss = styled('input', {})

export const CheckBoxWrapperCss = {
  position: 'relative',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  mr: '$space$12',
  width: 20,
  height: 20,
  background: 'transparent',
  border: '2px solid',
  borderColor: '$inputBorder',
  borderRadius: '$radii$xs',
  transition: 'all 250ms cubic-bezier(.4, .0, .23, 1)',
}

export const CheckBoxTickCss = {
  content: '',
  position: 'absolute',
  top: 9,
  left: 2,
  borderRight: '2px solid transparent',
  borderBottom: '2px solid transparent',
  transform: 'rotate(45deg)',
  transformOrigin: '0% 100%',
  animation: `${checkboxCheck} 125ms 250ms cubic-bezier(.4, .0, .23, 1) forwards`,
}

export const InputBoxWrap = styled(Flex, {
  'width': '100%',
  'input[type="checkbox"], input[type="radio"]': {
    'position': 'absolute',
    'opacity': 0,
    'pointerEvents': 'none',
    '& ~ label': {
      'display': 'flex',
      'alignItems': 'center',
      'flexWrap': 'wrap',
      'cursor': 'pointer',
      'span': {
        ...CheckBoxWrapperCss,
      },
      '&:hover': {
        span: {
          background: '$checkboxBgHover',
          borderColor: '$checkboxBorderHover',
        },
      },
    },
    '&:checked': {
      '& ~ label': {
        'span': {
          'backgroundColor': '$checkboxBg',
          'borderColor': '$checkboxBorder',
          'animation': `${shrinkBounce} 250ms cubic-bezier(.4, .0, .23, 1)`,
          '&:before': {
            ...CheckBoxTickCss,
          },
        },
        '&:hover': {
          span: {
            background: '$checkboxBgHover',
            borderColor: '$checkboxBorderHover',
          },
        },
      },
    },
  },
})
